import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Flame, Trophy, MessageSquare, Target, TrendingUp, Star } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useExperienceTheme } from '../context/ExperienceThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0f0f15] border border-white/10 rounded-xl p-3">
        <p className="text-white/60 text-xs">{label}</p>
        <p className="text-white font-medium">
          Rating: {payload[0].value}/5
        </p>
      </div>
    );
  }
  return null;
};

const MissionStats = () => {
  const navigate = useNavigate();
  const { currentTheme, experienceTheme } = useExperienceTheme();
  const [stats, setStats] = useState({
    mission_streak: 0,
    total_courage_logs: 0,
    bridge_signals_sent: 0,
    proactive_missions_completed: 0,
    mission_day: 1,
    recent_sessions: [],
  });
  const [loading, setLoading] = useState(true);

  // Theme terminology and colors
  const t = currentTheme?.terminology || {};
  const colors = currentTheme?.colors || { primary: '#00f3ff', secondary: '#bc13fe', accent: '#ff00aa' };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const chartData = stats.recent_sessions
    .filter((s) => s.mission_rating)
    .map((s) => ({
      date: new Date(s.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      rating: s.mission_rating,
    }))
    .reverse();

  const statCards = [
    {
      icon: Flame,
      label: t.streak || 'Mission Streak',
      value: stats.mission_streak,
      suffix: 'days',
      gradient: `from-[${colors.primary}]/20 to-[${colors.secondary}]/20`,
      iconColor: colors.primary,
    },
    {
      icon: Trophy,
      label: t.courageLogs || 'Courage Logs',
      value: stats.total_courage_logs,
      suffix: 'total',
      gradient: `from-[${colors.secondary}]/20 to-[${colors.accent}]/20`,
      iconColor: colors.secondary,
    },
    {
      icon: MessageSquare,
      label: t.signalsSent || 'Signals Sent',
      value: stats.bridge_signals_sent,
      suffix: 'sent',
      gradient: `from-[${colors.primary}]/20 to-[${colors.accent}]/20`,
      iconColor: colors.primary,
    },
    {
      icon: Target,
      label: 'Completed',
      value: stats.proactive_missions_completed,
      suffix: 'missions',
      gradient: `from-[${colors.accent}]/20 to-[${colors.primary}]/20`,
      iconColor: colors.accent,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen theme-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#00f3ff] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="theme-text-muted">Loading mission data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg">
      {/* Background */}
      <div className="fixed inset-0 theme-gradient opacity-50 pointer-events-none" />

      {/* Header */}
      <header className="relative z-20 px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center theme-text-muted hover:theme-text hover:bg-white/10 transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="theme-text text-lg md:text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t.missionStats || 'Mission Stats'}
            </h1>
            <p className="theme-text-muted text-sm">
              Your progress, {t.userRole || 'Pilot'}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-4 md:px-8 pb-12">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 stagger-children">
            {statCards.map((stat) => (
              <div
                key={stat.label}
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
                className="stat-card-modern"
                style={{ 
                  background: `linear-gradient(to bottom right, ${stat.iconColor}1a, ${colors.secondary}1a)` 
                }}
              >
                <stat.icon className="mb-3" size={24} style={{ color: stat.iconColor }} />
                <p className="theme-text text-2xl md:text-3xl font-bold">{stat.value}</p>
                <p className="theme-text-muted text-sm mt-1">{stat.label}</p>
                <p className="theme-text-muted text-xs">{stat.suffix}</p>
              </div>
            ))}
          </div>

          {/* Mission Trend Chart */}
          {chartData.length > 0 && (
            <div className="card-modern p-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${colors.secondary}33` }}>
                  <TrendingUp className="w-5 h-5" style={{ color: colors.secondary }} />
                </div>
                <div>
                  <h3 className="theme-text font-semibold">{t.missionStats || 'Mission'} Trend</h3>
                  <p className="theme-text-muted text-sm">Your daily ratings over time</p>
                </div>
              </div>

              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis
                      dataKey="date"
                      stroke="#666"
                      tick={{ fill: '#666', fontSize: 10 }}
                      axisLine={{ stroke: '#333' }}
                    />
                    <YAxis
                      domain={[0, 5]}
                      stroke="#666"
                      tick={{ fill: '#666', fontSize: 10 }}
                      axisLine={{ stroke: '#333' }}
                      ticks={[1, 2, 3, 4, 5]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="rating"
                      stroke="url(#gradient)"
                      strokeWidth={2}
                      dot={{ fill: colors.primary, strokeWidth: 0, r: 4 }}
                      activeDot={{ r: 6, fill: colors.secondary }}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={colors.primary} />
                        <stop offset="100%" stopColor={colors.secondary} />
                      </linearGradient>
                    </defs>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Recent Sessions */}
          <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <h3 className="theme-text-muted text-sm font-medium mb-4 uppercase tracking-wider">
              Recent {experienceTheme === 'sports' ? 'Games' : experienceTheme === 'pet' ? 'Days' : 'Missions'}
            </h3>

            {stats.recent_sessions.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_sessions.slice(0, 7).map((session) => {
                  const worryCount = session.worry_logs?.length || 0;
                  const freezeCount = session.freeze_logs?.length || 0;
                  const bridgeCount = session.bridge_messages?.length || 0;
                  const totalLogs = worryCount + freezeCount + bridgeCount;

                  return (
                    <div
                      key={session.id}
                      className="card-modern p-4 flex items-center justify-between"
                    >
                      <div>
                        <p className="theme-text font-medium text-sm">
                          {new Date(session.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </p>
                        <p className="theme-text-muted text-xs mt-1">
                          {totalLogs} logs
                          {session.mission_rating && ` • Rating: ${session.mission_rating}/5`}
                        </p>
                      </div>
                      {session.mission_rating && (
                        <div className="flex items-center gap-1">
                          {[...Array(session.mission_rating)].map((_, i) => (
                            <Star
                              key={i}
                              size={12}
                              style={{ color: colors.primary, fill: colors.primary }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="card-modern p-8 text-center">
                <p className="theme-text-muted">
                  No data yet. {t.dashboard || 'Mission Control'} is standing by.
                </p>
              </div>
            )}
          </div>

          {/* Encouragement */}
          <div className="text-center py-6 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <p className="theme-text-muted text-sm">
              {t.statsEncouragement || `Every log is a step forward. Keep going, ${t.userRole || 'Pilot'}.`}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MissionStats;
